import { SchedulesReportView } from "@common/types/views/schedulesReportView";
import React, { useContext, useEffect, useMemo } from "react";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { getPeriodKey } from "@common/helpers/reports";
import { SchedulesReportResponse } from "@common/schemas/domains/accounting/schedules-report.schema";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { ViewOptions } from "@common/types/views/views";
import { AccountingReportContext } from "../AccountingReport.context";
import {
    SchedulesReportTableCallbacks,
    SchedulesReportTableMeta,
} from "./schedulesReportDrillDown.lib";
import {
    buildSchedulesReportConfig,
    buildSchedulesReportView,
} from "./schedulesReportViewConfig";
import { SchedulesReportViewRow } from "./schedulesReportViewTypes";
import styles from "./SchedulesReportTable.module.scss";

interface Props {
    report: SchedulesReportResponse;
    rows: SchedulesReportViewRow[];
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    tableRef: React.RefObject<ViewTableHandle | null>;
    callbacks: SchedulesReportTableCallbacks;
    viewOptions?: ViewOptions | null;
    viewColumns?: SchedulesReportView["columns"];
}

export const SchedulesReportTable: React.FC<Props> = ({
    report,
    rows,
    expanded,
    onExpandedChange,
    tableRef,
    callbacks,
    viewOptions,
    viewColumns,
}) => {
    const { numberFormat } = useContext(AccountingReportContext);
    const tableMeta: SchedulesReportTableMeta = useMemo(
        () => ({ callbacks }),
        [callbacks],
    );
    const layout = report.meta.appliedLayout;
    const rowsMode = report.meta.appliedRows;
    const view = useMemo(
        () =>
            buildSchedulesReportView({
                periods: report.periods,
                layout,
                rows: rowsMode,
                options: viewOptions,
                columns: viewColumns,
            }),
        [report.periods, layout, rowsMode, viewOptions, viewColumns],
    );
    const config = useMemo(
        () =>
            buildSchedulesReportConfig({
                periods: report.periods,
                layout,
                rows: rowsMode,
                numberFormat,
            }),
        [report.periods, layout, rowsMode, numberFormat],
    );
    const reportKey = useMemo(
        () =>
            `${layout}|${rowsMode}|${report.periods.map(getPeriodKey).join("|")}`,
        [layout, rowsMode, report.periods],
    );

    useEffect(() => {
        const timerId = window.setTimeout(
            () => tableRef.current?.scrollTo({ left: 0 }),
            0,
        );
        return () => window.clearTimeout(timerId);
    }, [reportKey, tableRef]);

    return (
        <ViewTable
            key={reportKey}
            ref={tableRef}
            data={rows}
            view={view}
            config={config}
            className={styles.schedulesReportTable}
            tableMeta={tableMeta}
            rowBorders={false}
            enableColumnResizing
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            nestingIndentPx={16}
            fixedTopOffsetPx
        />
    );
};
